import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Authenticator } from '@aws-amplify/ui-react';
import { Typography } from '@mui/material';
import '@aws-amplify/ui-react/styles.css';

// Import Amplify configuration
import './services/amplify';

// Import components
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Accounts from './pages/Accounts';
import Users from './pages/Users';
import BulkUsers from './pages/BulkUsers';
import Admin from './pages/Admin';

// Create Material-UI theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

// Custom Authenticator components  
const components = {
  Header() {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <h1>User Management System</h1>
        <p>Sign in to manage users across AWS accounts</p>
        <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
          <em>User accounts are created by administrators only</em>
        </p>
      </div>
    );
  },



  // Custom SignIn component to emphasize admin-only access
  SignIn: {
    Header() {
      return (
        <Typography variant="h5" gutterBottom align="center">
          Sign In
        </Typography>
      );
    },
    Footer() {
      return (
        <Typography variant="body2" color="text.secondary" gutterBottom align="center">
          Only administrator-created accounts can sign in. Contact your administrator if you need access.
        </Typography>
      );
    }
  },

  // Custom Forgot Password component
  ForgotPassword: {
    Header() {
      return (
        <Typography variant="h5" gutterBottom align="center">
          Reset Password
        </Typography>
      );
    },
    Footer() {
      return (
        <Typography variant="body2" color="text.secondary" gutterBottom align="center">
          Enter your email address and we'll send you a password reset code.
        </Typography>
      );
    }
  },

  // Custom Confirm Reset Password component  
  ConfirmResetPassword: {
    Header() {
      return (
        <Typography variant="h5" gutterBottom align="center">
          Enter New Password
        </Typography>
      );
    },
    Footer() {
      return (
        <Typography variant="body2" color="text.secondary" gutterBottom align="center">
          Enter the verification code sent to your email and your new password.
        </Typography>
      );
    }
  },

  // Custom Force New Password component
  ForceNewPassword: {
    Header() {
      return (
        <Typography variant="h5" gutterBottom align="center">
          Set New Password
        </Typography>
      );
    },
    Footer() {
      return (
        <Typography variant="body2" color="text.secondary" gutterBottom align="center">
          Your administrator has reset your password. Please set a new password to continue.
        </Typography>
      );
    }
  }
};

// Configuration for signin and password reset only (no signup)
const formFields = {
  signIn: {
    username: {
      label: 'Email Address',
      placeholder: 'Enter your email address',
    },
    password: {
      label: 'Password',
      placeholder: 'Enter your password',
    },
  },
  forgotPassword: {
    username: {
      label: 'Email Address',
      placeholder: 'Enter your email address',
    },
  },
  confirmResetPassword: {
    username: {
      label: 'Email Address', 
      placeholder: 'Enter your email address',
    },
    confirmation_code: {
      label: 'Verification Code',
      placeholder: 'Enter code from email',
    },
    password: {
      label: 'New Password',
      placeholder: 'Enter new password',
    },
  },
  forceNewPassword: {
    password: {
      label: 'New Password',
      placeholder: 'Enter new password',
    },
  },
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Authenticator components={components} formFields={formFields} hideSignUp={true}>
        {({ signOut, user }) => (
          <Router>
            <Layout signOut={signOut} user={user}>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/accounts" element={<Accounts />} />
                <Route path="/users" element={<Users />} />
                <Route path="/bulk-users" element={<BulkUsers />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Layout>
          </Router>
        )}
      </Authenticator>
    </ThemeProvider>
  );
}

export default App;